import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FaTimes, FaSearch, FaChevronDown, FaChevronRight } from 'react-icons/fa';

/**
 * InlineFilterDropdown — compact multi-select filter pill with an async/searchable
 * dropdown, rendered via a portal (no overflow clipping). Designed for inline
 * filter bars (e.g. the ERP "quick filters" row).
 *
 * The value is the full array of selected `{ value, label }` objects so labels
 * survive without a static option list.
 *
 * `loadOptions` may return either:
 *   - a flat list:    [{ value, label }, ...]
 *   - a grouped list: [{ value?, label, options: [{ value, label }, ...] }, ...]
 *     A group with its own `value` is itself selectable (its checkbox lives in the
 *     group header); its children render indented below.
 *
 * Props:
 *   label       — trigger label shown when nothing is selected
 *   value       — array of selected { value, label }
 *   onChange    — (nextValue[]) => void
 *   loadOptions — async (searchText) => Promise<options>
 *   searchable  — show the search box (default true)
 *   minWidth    — dropdown min-width in px (default 240)
 *   className   — extra classes on the wrapper
 */
const isGroup = (o) => Array.isArray(o.options);

const InlineFilterDropdown = ({ label, value, onChange, loadOptions, searchable, minWidth, className }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const triggerRef = useRef(null);
  // Callers pass loadOptions inline; read the latest one without re-running effects.
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;
  // Only the newest request may fill the list — a slow earlier search must not
  // overwrite the results of the text typed after it.
  const requestSeqRef = useRef(0);

  const runLoad = useCallback(async (text) => {
    if (!loadOptionsRef.current) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const result = await loadOptionsRef.current(text);
      if (seq === requestSeqRef.current) setOptions(Array.isArray(result) ? result : []);
    } catch (e) {
      if (seq === requestSeqRef.current) setOptions([]);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  // One load per open (empty search, immediately), then one per debounced search.
  // A separate "load on open" effect used to fire alongside the debounced one, so
  // every open hit the API twice.
  useEffect(() => {
    if (!open) return undefined;
    if (search === '') {
      runLoad('');
      return undefined;
    }
    const timer = setTimeout(() => runLoad(search), 300);
    return () => clearTimeout(timer);
  }, [open, search, runLoad]);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.max(minWidth, rect.width);
      const left = Math.min(rect.left, window.innerWidth - width - 8);
      setPos({ top: rect.bottom + 4, left });
      setSearch('');
    }
    setOpen((o) => !o);
  };

  const isSelected = (opt) => value.some((v) => v.value === opt.value);

  const toggle = (opt) =>
    onChange(isSelected(opt) ? value.filter((v) => v.value !== opt.value) : [...value, { value: opt.value, label: opt.label }]);

  // Everything a group header stands for: its own value when it is selectable
  // (a parent warehouse holds stock of its own) plus every child.
  const groupMembers = (group) =>
    [...(group.value != null ? [{ value: group.value, label: group.label }] : []),
     ...group.options.map((child) => ({ value: child.value, label: child.label }))];

  const groupState = (group) => {
    const members = groupMembers(group);
    const picked = members.filter(isSelected).length;
    return { all: picked > 0 && picked === members.length, some: picked > 0 && picked < members.length };
  };

  // Checking the header selects the whole country, which is what people expect
  // from a grouped list; unchecking clears the group without touching the rest.
  const toggleGroup = (group) => {
    const members = groupMembers(group);
    const memberValues = new Set(members.map((m) => m.value));
    const remaining = value.filter((v) => !memberValues.has(v.value));

    onChange(groupState(group).all ? remaining : [...remaining, ...members]);
  };

  const isActive = value.length > 0;

  const renderOption = (opt, indent = false) => (
    <label
      key={opt.value}
      className={`tw-flex tw-items-center tw-gap-2.5 ${indent ? 'tw-pl-8' : 'tw-pl-3'} tw-pr-3 tw-py-1.5 hover:tw-bg-primary-50 tw-cursor-pointer tw-text-sm tw-text-slate-700 tw-select-none`}
    >
      <input
        type="checkbox"
        checked={isSelected(opt)}
        onChange={() => toggle(opt)}
        className="tw-accent-primary-600 tw-w-3.5 tw-h-3.5 tw-flex-shrink-0"
      />
      <span className="tw-text-xs tw-truncate">{opt.label}</span>
    </label>
  );

  const renderGroup = (group) => {
    const isCollapsed = collapsed[group.label];
    const { all, some } = groupState(group);
    return (
      <div key={group.label}>
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-1.5 tw-bg-slate-50 tw-border-b tw-border-slate-100">
          <button
            type="button"
            onClick={() => setCollapsed((p) => ({ ...p, [group.label]: !isCollapsed }))}
            className="tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-text-slate-400 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-flex-shrink-0"
          >
            <FaChevronRight size={8} className={`tw-transition-transform ${isCollapsed ? '' : 'tw-rotate-90'}`} />
          </button>
          <input
            type="checkbox"
            checked={all}
            // Partial selection reads as "some of this country", not "none".
            ref={(el) => { if (el) el.indeterminate = some; }}
            onChange={() => toggleGroup(group)}
            title={`Select all in ${group.label}`}
            className="tw-accent-primary-600 tw-w-3.5 tw-h-3.5 tw-flex-shrink-0"
          />
          <span className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-flex-1 tw-truncate">{group.label}</span>
          {/* Counts what the header selects, so it matches the badge on the button. */}
          <span className="tw-text-xs tw-text-slate-400">{groupMembers(group).length}</span>
        </div>
        {!isCollapsed && (
          <>
            {/* A parent that carries its own value is a real entry too (a country
                warehouse holds stock directly), so it gets its own row — the header
                checkbox means "all of them", this one means "just this warehouse". */}
            {group.value != null && renderOption({ value: group.value, label: group.label }, true)}
            {group.options.map((child) => renderOption(child, true))}
          </>
        )}
      </div>
    );
  };

  return (
    <div ref={triggerRef} className={`tw-inline-block ${className || ''}`}>
      <button
        type="button"
        onClick={handleToggle}
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded tw-border tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-colors tw-whitespace-nowrap ${
          isActive
            ? 'tw-bg-primary-50 tw-border-primary-400 tw-text-primary-700'
            : 'tw-bg-white tw-border-slate-300 tw-text-slate-600 hover:tw-border-primary-400 hover:tw-text-primary-600'
        }`}
      >
        <span>{isActive ? `${label}: ${value.length}` : label}</span>
        {isActive ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            onKeyDown={(e) => e.key === 'Enter' && onChange([])}
            className="tw-flex tw-items-center tw-justify-center tw-w-3.5 tw-h-3.5 tw-rounded-xl tw-bg-primary-200 hover:tw-bg-red-200 tw-cursor-pointer tw-transition-colors"
          >
            <FaTimes size={7} className="tw-text-primary-700" />
          </span>
        ) : (
          <FaChevronDown size={9} className="tw-text-slate-400" />
        )}
      </button>

      {open && ReactDOM.createPortal(
        <>
          <div className="tw-fixed tw-inset-0 tw-z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="tw-fixed tw-z-[9999] tw-bg-white tw-rounded tw-shadow-xl tw-border tw-border-slate-200 tw-flex tw-flex-col tw-max-h-80"
            style={{ top: pos.top, left: pos.left, minWidth }}
          >
            {searchable && (
              <div className="tw-relative tw-flex tw-items-center tw-p-2 tw-border-b tw-border-slate-100">
                <FaSearch className="tw-absolute tw-left-4 tw-text-slate-400 tw-pointer-events-none" size={11} />
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="tw-w-full tw-pl-7 tw-pr-2 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 tw-text-slate-700"
                />
              </div>
            )}

            <div className="tw-overflow-y-auto tw-py-1">
              {loading ? (
                <div className="tw-px-3 tw-py-3 tw-text-xs tw-text-slate-400 tw-text-center">Loading...</div>
              ) : options.length === 0 ? (
                <div className="tw-px-3 tw-py-3 tw-text-xs tw-text-slate-400 tw-text-center">No results</div>
              ) : (
                options.map((opt) => (isGroup(opt) ? renderGroup(opt) : renderOption(opt)))
              )}
            </div>

            {isActive && (
              <div className="tw-border-t tw-border-slate-100">
                <button
                  type="button"
                  onClick={() => { onChange([]); setOpen(false); }}
                  className="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-text-red-500 hover:tw-bg-red-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                >
                  Clear ({value.length})
                </button>
              </div>
            )}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

InlineFilterDropdown.propTypes = {
  label:       PropTypes.string.isRequired,
  value:       PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.any, label: PropTypes.string })).isRequired,
  onChange:    PropTypes.func.isRequired,
  loadOptions: PropTypes.func.isRequired,
  searchable:  PropTypes.bool,
  minWidth:    PropTypes.number,
  className:   PropTypes.string,
};

InlineFilterDropdown.defaultProps = {
  searchable: true,
  minWidth: 240,
  className: '',
};

export default InlineFilterDropdown;
